/**
 * The client half of professorx's `IdempotentScheduleKey.canonicalizeOptions`
 * (professorx tasks/resilience/IdempotentScheduleKey.kt:276-325).
 *
 * WHY THIS EXISTS AT ALL. The editor has to answer one question before every export: has
 * anything export-affecting changed since the last submission? If the answer is "no", the
 * same `(projectId, revision)` replays the first render (STUDIO_DESIGN.md 3.3). If the
 * answer is wrongly "yes", the editor bumps `revision` and the user pays for a second,
 * identical render.
 *
 * The server decides sameness with `canonicalizeOptions`, which SORTS OBJECT KEYS
 * RECURSIVELY. A plain `JSON.stringify` does not, so a pure key reorder -- a clip rebuilt
 * with its properties assigned in a different order, which happens every time an object
 * literal is edited -- would read as a change here and as no change there. That is the
 * over-bump, and it is the expensive direction.
 *
 * WHAT IS DELIBERATELY NOT PORTED: the server's number normalisation
 * (`BigDecimal.stripTrailingZeros`, IdempotentScheduleKey.kt:327-334, which folds `1.0`,
 * `1` and `1e0` together). The client is the PRODUCER of this JSON: every value it emits
 * is a JS `number` or a `string`, `JSON.stringify(1.0)` is `'1'` in every engine, and
 * `toWire()` rounds every millisecond field to an integer -- so there is no pair of client
 * outputs the two canonicalisations could disagree about. Porting BigDecimal would add a
 * second source of truth for a case that cannot arise.
 *
 * Array order is preserved because it is semantic on both sides (clip order IS the
 * timeline). String and number stay distinct, because the server keeps them distinct.
 */

/**
 * Key order. Kotlin's `sortedBy { it.key }` compares Strings by UTF-16 code unit, and JS's
 * default `Array.prototype.sort()` comparator compares by UTF-16 code unit as well, so the
 * two orderings are the same function. This is stated rather than assumed because every
 * "sort the keys" bug is a locale-aware comparator someone reached for by reflex.
 */
function sortedKeys(o: object): string[] {
    return Object.keys(o).filter((k) => (o as Record<string, unknown>)[k] !== undefined).sort();
}

function write(value: unknown): string {
    if (value === null || value === undefined) return 'null';
    if (Array.isArray(value)) return `[${value.map(write).join(',')}]`;
    if (typeof value === 'object') {
        const keys = sortedKeys(value as object);
        const body = keys
            .map((k) => `${JSON.stringify(k)}:${write((value as Record<string, unknown>)[k])}`)
            .join(',');
        return `{${body}}`;
    }
    if (typeof value === 'number') {
        // NaN and +/-Infinity have no JSON form; `JSON.stringify` writes `null` for them and
        // so does this, rather than emitting a token no parser accepts.
        return Number.isFinite(value) ? JSON.stringify(value) : 'null';
    }
    return JSON.stringify(value);
}

/** A stable string for a value: object keys sorted recursively, array order untouched. */
export function canonicalJson(value: unknown): string {
    return write(value);
}
